import produce from 'immer';
import {useCallback, useMemo} from 'react';

import DocumentStore from 'notebook/documents/DocumentStore';
import {StepGroupUpdater} from 'procedures/design/step-config/StepConfigEditor';
import {collectAllSteps} from 'procedures/design/step-config/stepManagementUtils';
import {ProcedureStepGroup} from 'procedures/ProcedureDomainTypes';

export interface UseDocumentEditorManagerParams {
  stepGroups: Array<ProcedureStepGroup>;
  onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void;
}

export interface UseDocumentEditorManagerResult {
  relatedDocumentIds: Array<string>;
  handleDocumentOnChange: (wasServerUpdate: boolean, changedDocumentId: string) => void;
}

export default function useDocumentEditorManager(
  params: UseDocumentEditorManagerParams
): UseDocumentEditorManagerResult {
  const {stepGroups, onChangeSteps} = params;

  const relatedDocumentIds = useMemo(
    () =>
      collectAllSteps(stepGroups)
        .filter((step) => step.documentId != null)
        .map((step) => step.documentId!),
    [stepGroups]
  );

  const handleDocumentOnChange = useCallback(
    (wasServerUpdate: boolean, changedDocumentId: string) => {
      if (DocumentStore.hasUnsavedChanges(changedDocumentId)) {
        onChangeSteps((stepGroups) =>
          produce(stepGroups, (draftStepGroups) => {
            const step = collectAllSteps(draftStepGroups).find((s) => s.documentId === changedDocumentId);
            if (step) {
              step.documentVersionUpdate = DocumentStore.getDocumentVersionUpdate(changedDocumentId);
            }
          })
        );
      }
    },
    [onChangeSteps]
  );

  return {
    relatedDocumentIds,
    handleDocumentOnChange,
  };
}
